import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { formatPrice } from "../../utils/currency";
import "./SuperAdminDashboard.css";

function getToday() {
  return new Date().toISOString().slice(0, 10);
}
function getMonthStart() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function getMonthEnd() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
}
function formatDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function VenueAdminDashboard() {
  const { api, logout, isVenueAdmin, venueId } = useAuth();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [todaySummary, setTodaySummary] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState(getMonthStart());
  const [dateTo, setDateTo] = useState(getMonthEnd());
  const [statusFilter, setStatusFilter] = useState("");

  const fetchVenue = useCallback(async () => {
    if (!venueId) return;
    try {
      const res = await api(
        `query($vid: Int) { venue(venue_id: $vid) { venue_id name slug address is_active } }`,
        { vid: venueId }
      );
      setVenue(res.data?.data?.venue || null);
    } catch (err) {
      setError(err.message || "Failed to load venue");
    }
  }, [api, venueId]);

  const fetchSummaries = useCallback(async () => {
    if (!venueId) return;
    try {
      const today = getToday();
      const monthStart = getMonthStart();
      const monthEnd = getMonthEnd();
      const res1 = await api(
        `query($vid: Int, $today: String) {
          salesSummary(venue_id: $vid, date_from: $today, date_to: $today) { total_orders total_revenue }
        }`,
        { vid: venueId, today }
      );
      const res2 = await api(
        `query($vid: Int, $ms: String, $me: String) {
          salesSummary(venue_id: $vid, date_from: $ms, date_to: $me) { total_orders total_revenue }
        }`,
        { vid: venueId, ms: monthStart, me: monthEnd }
      );
      setTodaySummary(res1.data?.data?.salesSummary || {});
      setMonthSummary(res2.data?.data?.salesSummary || {});
    } catch (err) {
      setError(err.message || "Failed to load summaries");
    } finally {
      setLoading(false);
    }
  }, [api, venueId]);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await api(
        `query($status: String, $date_from: String, $date_to: String, $venue_id: Int, $limit: Int, $offset: Int) {
          orders(status: $status, date_from: $date_from, date_to: $date_to, venue_id: $venue_id, limit: $limit, offset: $offset) {
            order_id order_number customer_token customer_name total_amount order_status kiosk_type created_at
          }
        }`,
        {
          status: statusFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          venue_id: venueId,
          limit: 100,
          offset: 0,
        }
      );
      setOrders(res.data?.data?.orders || []);
    } catch (err) {
      setOrders([]);
      setError(err.message);
    } finally {
      setOrdersLoading(false);
    }
  }, [api, dateFrom, dateTo, statusFilter, venueId]);

  useEffect(() => {
    if (!isVenueAdmin || !venueId) return;
    fetchVenue();
    fetchSummaries();
  }, [isVenueAdmin, venueId, fetchVenue, fetchSummaries]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleLogout = () => {
    logout();
    navigate("/venue-admin/login");
  };

  if (!isVenueAdmin || !venueId) {
    return (
      <div className="admin-dashboard">
        <p>Unauthorized. Redirecting...</p>
        {setTimeout(() => navigate("/venue-admin/login"), 1000)}
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-dashboard-header">
        <div>
          <h1>Venue Admin</h1>
          <p>{venue?.name || `Venue ${venueId}`}</p>
        </div>
        <button onClick={handleLogout} className="btn-logout">
          Logout
        </button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-section">
        <h2>Analytics</h2>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="kpi-grid">
            <div className="kpi-card">
              <h3>Today</h3>
              <div>{todaySummary?.total_orders ?? 0} orders</div>
              <div>{formatPrice(todaySummary?.total_revenue ?? 0)}</div>
            </div>
            <div className="kpi-card">
              <h3>This Month</h3>
              <div>{monthSummary?.total_orders ?? 0} orders</div>
              <div>{formatPrice(monthSummary?.total_revenue ?? 0)}</div>
            </div>
          </div>
        )}
      </section>

      <section className="admin-section">
        <h2>Orders</h2>
        <div className="filters">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="cooking">Cooking</option>
            <option value="ready">Ready</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button onClick={fetchOrders}>Apply</button>
        </div>
        <div className="orders-table-wrap">
          {ordersLoading ? (
            <div>Loading orders...</div>
          ) : (
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id}>
                    <td>{formatDateTime(o.created_at)}</td>
                    <td>{o.order_number || o.order_id}</td>
                    <td>{o.customer_name || "—"}</td>
                    <td>{formatPrice(o.total_amount || 0)}</td>
                    <td><span className={`status-badge status-${o.order_status || "pending"}`}>{o.order_status || "pending"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!ordersLoading && orders.length === 0 && <div>No orders in this range.</div>}
        </div>
      </section>
    </div>
  );
}

export default VenueAdminDashboard;
